import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { PrivatePageLayout, PageHeader, PageTableCard } from '../../components/PrivatePage';
import NewUserButton from './NewUserButton';
import UserRow from './UserRow';
import Pagination from '../../components/Pagination/Pagination';
import { getUsers, deleteUser, startUser, stopUser, getUserDetails } from '../../services/UsersService';
import Toast from '../../components/Toast/Toast';
import SearchUser from './SearchUser';
import UserModal from './UserModal/UserModal';
import UserDetailsModal from './UserDetailsModal';

function Users() {
    const defaultLocation = useLocation();
    const history = useHistory();
    const [users, setUsers] = useState([]);
    const [editUser, setEditUser] = useState({});
    const [count, setCount] = useState(0);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(getPage());
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [userDetails, setUserDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState('');

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    useEffect(() => {
        return history.listen((location) => {
            setPage(getPage(location));
        });
    }, [history]);

    useEffect(() => {
        getUsers(search, page || 1)
            .then((result) => {
                setUsers(result.rows);
                setCount(result.count);
            })
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }, [page, search]);

    function onDetailsClick(event) {
        const id = event.currentTarget.id.replace('details', '');
        setUserDetails(null);
        setDetailsError('');
        setDetailsLoading(true);
        getUserDetails(id)
            .then((result) => setUserDetails(result))
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setDetailsError(err.response ? err.response.data : err.message);
            })
            .finally(() => setDetailsLoading(false));
    }

    function onEditClick(event) {
        const id = event.target.id.replace('edit', '');
        setEditUser(users.find((u) => u.id == id));
    }

    function onStopClick(event) {
        const id = event.target.id.replace('stop', '');
        stopUser(id)
            .then(() => { history.go(0); })
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }

    function onStartClick(event) {
        const id = event.target.id.replace('start', '');
        startUser(id)
            .then(() => { history.go(0); })
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }

    function onDeleteClick(event) {
        const id = event.target.id.replace('delete', '');
        deleteUser(id)
            .then(() => { history.go(0); })
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }

    function onSearchChange(event) {
        setSearch(event.target.value);
    }

    function onModalSubmit() {
        history.go(0);
    }

    return (
        <PrivatePageLayout>
            <PageHeader
                eyebrow="Admin"
                title="Users"
                subtitle="Create, start, stop, and manage platform accounts."
                actions={(
                    <div className="private-page-header-filters">
                        <NewUserButton />
                        <SearchUser placeholder={search} onChange={onSearchChange} />
                    </div>
                )}
            />
            <PageTableCard>
                <div className="table-wrapper table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Limit</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users && users.length
                                ? users.map((user) => (
                                    <UserRow
                                        key={user.id}
                                        data={user}
                                        onDetailsClick={onDetailsClick}
                                        onEditClick={onEditClick}
                                        onStartClick={onStartClick}
                                        onStopClick={onStopClick}
                                        onDeleteClick={onDeleteClick}
                                    />
                                ))
                                : null}
                        </tbody>
                    </table>
                </div>
                <Pagination count={count} />
            </PageTableCard>
            <UserDetailsModal data={userDetails} loading={detailsLoading} error={detailsError} />
            <UserModal data={editUser} onSubmit={onModalSubmit} />
            <Toast type={notification.type} text={notification.text} />
        </PrivatePageLayout>
    );
}

export default Users;
